/**
 * 聊天 store（架构 §2 src/store/chat.store.ts / T09 / T14 / T15 / T16）
 *  - conversations：我的会话列表
 *  - messages：convId -> Message[]（实时事件驱动）
 *  - members：convId -> Member[]
 *  - aiTyping：convId -> 是否正在"知会思考"
 *  - activeConversationId：当前打开的会话
 *
 * 由 socket/client.ts 在收到 PartyKit 事件时调用这里的 action 驱动更新。
 */
import { create } from 'zustand';
import type { Conversation, Member, Message } from '../types/models';
import { isTrue } from '../types/models';
import type { MentionItem } from '../components/chat/MentionPopover';

export interface AIStreamState {
  stream_id: string;
  source_message_id?: number;
  content: string;
  lastSeq: number;
  status: 'streaming' | 'retrying';
  started_at: string;
}

interface ChatState {
  conversations: Conversation[];
  messages: Record<number, Message[]>;
  members: Record<number, Member[]>;
  aiTyping: Record<number, boolean>;
  aiStreams: Record<number, Record<string, AIStreamState>>;
  activeConversationId: number | null;
  /** 初始会话列表是否已加载完成（区分「加载中」与「确实不存在」，供 ChatPage 深链接判断） */
  conversationsLoaded: boolean;
  /** CP14：会话未读计数（convId -> 数量），以后端 unread_count 为基准，WS 新消息实时 +1 */
  unread: Record<number, number>;

  setConversations: (list: Conversation[]) => void;
  upsertConversation: (conv: Conversation) => void;
  setMembers: (conversationId: number, members: Member[]) => void;

  /** 用历史页替换某会话消息（首次进入/刷新） */
  setMessages: (conversationId: number, msgs: Message[]) => void;
  /** 向前追加更旧的历史（load more） */
  prependMessages: (conversationId: number, msgs: Message[]) => void;
  /** 收到新消息：按 id 去重后追加到末尾 */
  appendMessage: (conversationId: number, msg: Message) => void;

  setAiTyping: (conversationId: number, on: boolean) => void;
  startAiStream: (conversationId: number, streamId: string, sourceMessageId?: number) => void;
  appendAiStreamDelta: (conversationId: number, streamId: string, seq: number, delta: string) => void;
  markAiStreamRetrying: (conversationId: number, streamId: string) => void;
  endAiStream: (conversationId: number, streamId: string) => void;
  setActive: (conversationId: number | null) => void;

  /** 拉知会成功后更新会话标志 */
  markAiMember: (conversationId: number) => void;

  /** CP13：当前正在引用的消息（回复编辑条），null 表示无引用 */
  replyTarget: Message | null;
  setReplyTarget: (m: Message | null) => void;

  /** CP14：未读计数操作 */
  setUnread: (conversationId: number, n: number) => void;
  incUnread: (conversationId: number) => void;
  clearUnread: (conversationId: number) => void;

  reset: () => void;
}

function dedupeAppend(existing: Message[] | undefined, msg: Message): Message[] {
  const list = existing ? [...existing] : [];
  if (list.some((m) => m.id === msg.id)) return list;
  list.push(msg);
  // 按 id 升序，保证顺序稳定
  list.sort((a, b) => a.id - b.id);
  return list;
}

export const useChatStore = create<ChatState>((set, get) => ({
  conversations: [],
  messages: {},
  members: {},
  aiTyping: {},
  aiStreams: {},
  activeConversationId: null,
  conversationsLoaded: false,
  unread: {},
  replyTarget: null,

  setConversations: (list) =>
    set({
      conversations: list,
      conversationsLoaded: true,
      // CP14：以后端 unread_count 为基准初始化未读（刷新列表时对齐服务端真相）
      unread: Object.fromEntries(list.map((c) => [c.id, c.unread_count ?? 0])),
    }),
  upsertConversation: (conv) => {
    const list = get().conversations.slice();
    const idx = list.findIndex((c) => c.id === conv.id);
    if (idx >= 0) list[idx] = conv;
    else list.unshift(conv);
    set({ conversations: list });
  },
  setMembers: (conversationId, members) =>
    set((s) => ({ members: { ...s.members, [conversationId]: members } })),

  setMessages: (conversationId, msgs) =>
    set((s) => ({ messages: { ...s.messages, [conversationId]: [...msgs] } })),
  prependMessages: (conversationId, msgs) =>
    set((s) => {
      const existing = s.messages[conversationId] ?? [];
      const map = new Map<number, Message>();
      for (const m of [...msgs, ...existing]) map.set(m.id, m);
      const merged = Array.from(map.values()).sort((a, b) => a.id - b.id);
      return { messages: { ...s.messages, [conversationId]: merged } };
    }),
  appendMessage: (conversationId, msg) =>
    set((s) => {
      const messages = {
        ...s.messages,
        [conversationId]: dedupeAppend(s.messages[conversationId], msg),
      };
      // CP13：新消息同步刷新会话列表预览（末条内容 + 时间 + 发送者）
      const conversations = s.conversations.map((c) =>
        c.id === conversationId
          ? {
              ...c,
              last_message: msg.content,
              last_message_at: msg.created_at,
              last_message_sender_id: msg.sender_id,
              last_message_sender_type: msg.sender_type,
              last_message_id: msg.id,
            }
          : c,
      );
      // CP14：非当前会话收到新消息 → 未读 +1（当前会话内的消息不计未读，由 ChatPage 进入即清零）
      const unread =
        s.activeConversationId === conversationId
          ? s.unread
          : { ...s.unread, [conversationId]: (s.unread[conversationId] ?? 0) + 1 };
      return { messages, conversations, unread };
    }),

  setAiTyping: (conversationId, on) =>
    set((s) => ({ aiTyping: { ...s.aiTyping, [conversationId]: on } })),
  startAiStream: (conversationId, streamId, sourceMessageId) =>
    set((s) => ({
      aiStreams: {
        ...s.aiStreams,
        [conversationId]: {
          ...(s.aiStreams[conversationId] ?? {}),
          [streamId]: {
            stream_id: streamId,
            source_message_id: sourceMessageId,
            content: '',
            lastSeq: 0,
            status: 'streaming',
            started_at: new Date().toISOString(),
          },
        },
      },
    })),
  appendAiStreamDelta: (conversationId, streamId, seq, delta) =>
    set((s) => {
      const current = s.aiStreams[conversationId]?.[streamId] ?? {
        stream_id: streamId,
        content: '',
        lastSeq: 0,
        status: 'streaming' as const,
        started_at: new Date().toISOString(),
      };
      if (seq <= current.lastSeq) return {};
      return {
        aiStreams: {
          ...s.aiStreams,
          [conversationId]: {
            ...(s.aiStreams[conversationId] ?? {}),
            [streamId]: {
              ...current,
              content: current.content + delta,
              lastSeq: seq,
              status: 'streaming' as const,
            },
          },
        },
      };
    }),
  markAiStreamRetrying: (conversationId, streamId) =>
    set((s) => {
      const current = s.aiStreams[conversationId]?.[streamId];
      if (!current) return {};
      return {
        aiStreams: {
          ...s.aiStreams,
          [conversationId]: {
            ...s.aiStreams[conversationId],
            [streamId]: { ...current, status: 'retrying' as const },
          },
        },
      };
    }),
  endAiStream: (conversationId, streamId) =>
    set((s) => {
      const streams = { ...(s.aiStreams[conversationId] ?? {}) };
      delete streams[streamId];
      return { aiStreams: { ...s.aiStreams, [conversationId]: streams } };
    }),
  setActive: (conversationId) => set({ activeConversationId: conversationId }),

  setReplyTarget: (m) => set({ replyTarget: m }),

  setUnread: (conversationId, n) =>
    set((s) => ({ unread: { ...s.unread, [conversationId]: n } })),
  incUnread: (conversationId) =>
    set((s) => ({ unread: { ...s.unread, [conversationId]: (s.unread[conversationId] ?? 0) + 1 } })),
  clearUnread: (conversationId) =>
    set((s) => ({ unread: { ...s.unread, [conversationId]: 0 } })),

  markAiMember: (conversationId) =>
    set((s) => ({
      conversations: s.conversations.map((c) =>
        c.id === conversationId ? { ...c, is_ai_member: 1 } : c,
      ),
    })),

  reset: () =>
    set({ conversations: [], messages: {}, members: {}, aiTyping: {}, aiStreams: {}, activeConversationId: null, conversationsLoaded: false, unread: {}, replyTarget: null }),
}));

/** 判断某会话是否已拉入知会（供 ChatPage 显隐 @知会 / AI 能力） */
export function isAiMember(conv: Conversation | undefined): boolean {
  return isTrue(conv?.is_ai_member);
}

/**
 * @提及候选：取会话成员（排除自己）。
 *  - is_ai=1 的成员映射为「知会」(isAi=true)；
 *  - 普通成员映射为 {id, name=username, role}。
 * 供 InputBar 的 @ 弹层使用（替代旧的 useUserStore.knownUsers 来源）。
 */
export function mentionCandidates(
  convId: number,
  meId: number,
): MentionItem[] {
  const members = useChatStore.getState().members[convId] ?? [];
  const items: MentionItem[] = [];
  for (const m of members) {
    if (m.user_id === meId) continue;
    if (m.is_ai === 1) {
      items.push({ id: 'ai', name: '知会', isAi: true, role: 'ai' });
    } else {
      items.push({
        id: String(m.user_id),
        name: m.username ?? '成员',
        role: m.role,
      });
    }
  }
  return items;
}

/** 与知会私聊（AI DM）判定，复用 types 定义 */
export { isAiDm } from '../types/models';
